import {
  SkyButton,
  SkyDropdown,
  SkyInput,
  SkyRadioGroup,
  SkyText,
  SkyTextarea,
} from "@skypointcloud/skypoint-ui";
import { useId, useState } from "react";
import {
  GA4_FILTER_OPERATORS,
  GA4_FILTER_OPERATOR_LABELS,
  emptyGa4Condition,
  ga4BuilderFromFilter,
  ga4BuilderProblem,
  ga4FilterFromBuilder,
  parseGa4FilterText,
  type Ga4FilterBuilder,
  type Ga4FilterCondition,
  type Ga4FilterOperator,
} from "@shared/connectors/ga4Report";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { AddIcon, DeleteIcon } from "@/lib/icons";

/**
 * #2428 (R9): a report's filter, built from flat conditions joined by all or any, with a Not tick
 * per condition, or written as a GA4 filter in JSON. Both edit the same object
 * (`shared/connectors/ga4Report.ts` converts both ways); a filter the builder cannot show without
 * loss (nested groups, a partial pattern) is edited as JSON only. A condition's field is typed,
 * with the report's dimensions offered as suggestions, never a closed list: a filter may name a
 * field the report does not request.
 */

export type FilterMode = "builder" | "json";

export interface ReportFilterState {
  mode: FilterMode;
  builder: Ga4FilterBuilder;
  json: string;
  /** True when the stored filter could not be shown in the builder. */
  jsonOnly: boolean;
}

export function filterStateOf(
  filter: Record<string, unknown> | null | undefined,
): ReportFilterState {
  const value = filter && Object.keys(filter).length > 0 ? filter : null;
  const builder = ga4BuilderFromFilter(value);
  if (builder)
    return { mode: "builder", builder, json: value ? pretty(value) : "", jsonOnly: false };
  return {
    mode: "json",
    builder: { join: "all", conditions: [] },
    json: pretty(value),
    jsonOnly: true,
  };
}

function pretty(value: unknown): string {
  return value ? JSON.stringify(value, null, 2) : "";
}

/** The filter the editor holds, or the sentence its JSON earns. */
export function filterOf(
  state: ReportFilterState,
): { ok: true; filter: Record<string, unknown> | null } | { ok: false; sentence: string } {
  if (state.mode === "builder") {
    const sentence = ga4BuilderProblem(state.builder);
    return sentence
      ? { ok: false, sentence }
      : { ok: true, filter: ga4FilterFromBuilder(state.builder) };
  }
  return parseGa4FilterText(state.json);
}

const STRING_OPERATORS = new Set<Ga4FilterOperator>([
  "exact",
  "begins_with",
  "ends_with",
  "contains",
  "full_regexp",
  "in_list",
]);

const OPERATOR_OPTIONS = GA4_FILTER_OPERATORS.map((value) => ({
  value,
  text: GA4_FILTER_OPERATOR_LABELS[value],
}));

const JOIN_OPTIONS = [
  { value: "all", label: "All conditions" },
  { value: "any", label: "Any condition" },
];

export function ReportFilterEditor({
  state,
  onChange,
  dimensions,
  readOnly = false,
  error,
}: {
  state: ReportFilterState;
  onChange: (next: ReportFilterState) => void;
  /** The report's dimensions, offered as suggestions for a condition's field. */
  dimensions: readonly string[];
  readOnly?: boolean;
  /** The sentence the filter earns, said under it. */
  error?: string | null;
}) {
  const suggestions = useId();
  const errorId = useId();
  const setBuilder = (builder: Ga4FilterBuilder) => onChange({ ...state, builder });
  // A stable key per condition row, so removing one never hands its neighbour's controls state.
  const [rowKeys, setRowKeys] = useState<{ next: number; keys: number[] }>({ next: 0, keys: [] });
  const count = state.builder.conditions.length;
  if (rowKeys.keys.length !== count) {
    const keys = rowKeys.keys.slice(0, count);
    let next = rowKeys.next;
    while (keys.length < count) keys.push(next++);
    setRowKeys({ next, keys });
  }
  const removeRow = (index: number) => {
    setRowKeys({ next: rowKeys.next, keys: rowKeys.keys.filter((_, at) => at !== index) });
    setBuilder({
      ...state.builder,
      conditions: state.builder.conditions.filter((_, at) => at !== index),
    });
  };
  const setCondition = (index: number, patch: Partial<Ga4FilterCondition>) =>
    setBuilder({
      ...state.builder,
      conditions: state.builder.conditions.map((condition, at) =>
        at === index ? { ...condition, ...patch } : condition,
      ),
    });

  const toJson = () => {
    const filter = ga4FilterFromBuilder(state.builder);
    onChange({ ...state, mode: "json", json: pretty(filter), jsonOnly: false });
  };
  const toBuilder = () => {
    const parsed = parseGa4FilterText(state.json);
    if (!parsed.ok) return;
    const builder = ga4BuilderFromFilter(parsed.filter);
    if (builder) onChange({ ...state, mode: "builder", builder, jsonOnly: false });
    else onChange({ ...state, jsonOnly: true });
  };

  return (
    <fieldset
      className="flex min-w-0 flex-col gap-2"
      aria-describedby={state.mode === "builder" && error ? errorId : undefined}
      data-testid="report-filter"
    >
      <legend className="mb-2">
        <SkyText as="h3" size={300} weight="semibold" block>
          Filter
        </SkyText>
      </legend>
      <SkyText as="p" size={200} color="muted" block>
        Optional. Show only the rows that match. Leave it empty to read every row.
      </SkyText>
      {state.mode === "builder" ? (
        <BuilderPane
          builder={state.builder}
          dimensions={dimensions}
          suggestions={suggestions}
          rowKeys={rowKeys.keys}
          readOnly={readOnly}
          setBuilder={setBuilder}
          setCondition={setCondition}
          removeRow={removeRow}
          toJson={toJson}
        />
      ) : (
        <JsonPane
          state={state}
          readOnly={readOnly}
          error={error}
          onChange={onChange}
          toBuilder={toBuilder}
        />
      )}
      {state.mode === "builder" && error ? (
        <SkyText
          id={errorId}
          size={200}
          color="danger"
          block
          role="alert"
          testId="report-filter-error"
        >
          {error}
        </SkyText>
      ) : null}
    </fieldset>
  );
}

/** The condition builder: the join, one row per condition, and the buttons under them. */
function BuilderPane({
  builder,
  dimensions,
  suggestions,
  rowKeys,
  readOnly,
  setBuilder,
  setCondition,
  removeRow,
  toJson,
}: {
  builder: Ga4FilterBuilder;
  dimensions: readonly string[];
  suggestions: string;
  rowKeys: readonly number[];
  readOnly: boolean;
  setBuilder: (builder: Ga4FilterBuilder) => void;
  setCondition: (index: number, patch: Partial<Ga4FilterCondition>) => void;
  removeRow: (index: number) => void;
  toJson: () => void;
}) {
  return (
    <>
      {builder.conditions.length > 1 ? (
        <SkyRadioGroup
          label="Show rows that match"
          layout="horizontal"
          options={JOIN_OPTIONS}
          value={builder.join}
          disabled={readOnly}
          testId="report-filter-join"
          onChange={(join) => setBuilder({ ...builder, join: join as "all" | "any" })}
        />
      ) : null}
      <datalist id={suggestions}>
        {[...new Set(dimensions)].map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      {builder.conditions.map((condition, index) => (
        <ConditionRow
          key={rowKeys[index] ?? `new-${condition.fieldName}`}
          index={index}
          condition={condition}
          suggestions={suggestions}
          readOnly={readOnly}
          onChange={(patch) => setCondition(index, patch)}
          onRemove={() => removeRow(index)}
        />
      ))}
      {readOnly ? null : (
        <div className="flex flex-wrap gap-2">
          <SkyButton
            appearance="subtle"
            size="small"
            icon={AddIcon}
            testId="report-filter-add-condition"
            onClick={() =>
              setBuilder({
                ...builder,
                conditions: [...builder.conditions, emptyGa4Condition()],
              })
            }
          >
            Add a condition
          </SkyButton>
          <SkyButton
            appearance="subtle"
            size="small"
            testId="report-filter-to-json"
            onClick={toJson}
          >
            Edit as JSON
          </SkyButton>
        </div>
      )}
    </>
  );
}

/** The filter written as JSON, with the way back to the builder. */
function JsonPane({
  state,
  readOnly,
  error,
  onChange,
  toBuilder,
}: {
  state: ReportFilterState;
  readOnly: boolean;
  error?: string | null;
  onChange: (next: ReportFilterState) => void;
  toBuilder: () => void;
}) {
  return (
    <>
      {state.jsonOnly ? (
        <SkyText size={200} color="muted" block testId="report-filter-json-only">
          This filter is edited as JSON.
        </SkyText>
      ) : null}
      <SkyTextarea
        label="Filter as JSON"
        testId="report-filter-json"
        value={state.json}
        readOnly={readOnly}
        rows={6}
        resize="vertical"
        hint="A GA4 filter, for example a filter on hostName with an exact match."
        validationState={error ? "error" : "none"}
        validationMessage={error ?? undefined}
        onChange={(event) => onChange({ ...state, json: event.target.value })}
      />
      {readOnly ? null : (
        <div>
          <SkyButton
            appearance="subtle"
            size="small"
            testId="report-filter-to-builder"
            onClick={toBuilder}
          >
            Use the condition builder
          </SkyButton>
        </div>
      )}
    </>
  );
}

function ConditionRow({
  index,
  condition,
  suggestions,
  readOnly,
  onChange,
  onRemove,
}: {
  index: number;
  condition: Ga4FilterCondition;
  suggestions: string;
  readOnly: boolean;
  onChange: (patch: Partial<Ga4FilterCondition>) => void;
  onRemove: () => void;
}) {
  const number = index + 1;
  return (
    <fieldset
      className="flex flex-col gap-2 rounded-md border border-border p-3"
      aria-label={`Condition ${number}`}
      data-testid={`report-filter-condition-${index}`}
    >
      <SkyInput
        label="Field"
        size="small"
        list={suggestions}
        value={condition.fieldName}
        readOnly={readOnly}
        placeholder="e.g. hostName"
        testId={`report-filter-field-${index}`}
        onChange={(event) => onChange({ fieldName: event.target.value })}
      />
      <SkyDropdown
        label="Condition"
        size="small"
        options={OPERATOR_OPTIONS}
        selectedOptions={[condition.operator]}
        disabled={readOnly}
        testId={`report-filter-operator-${index}`}
        onOptionSelect={(values) => {
          const operator = values[0] as Ga4FilterOperator | undefined;
          if (operator) onChange({ operator });
        }}
      />
      {condition.operator === "in_list" ? (
        <SkyTextarea
          label="Values, one per line"
          size="small"
          rows={3}
          value={condition.value}
          readOnly={readOnly}
          testId={`report-filter-value-${index}`}
          onChange={(event) => onChange({ value: event.target.value })}
        />
      ) : (
        <SkyInput
          label={condition.operator === "between" ? "From" : "Value"}
          size="small"
          value={condition.value}
          readOnly={readOnly}
          testId={`report-filter-value-${index}`}
          onChange={(event) => onChange({ value: event.target.value })}
        />
      )}
      {condition.operator === "between" ? (
        <SkyInput
          label="To"
          size="small"
          value={condition.to}
          readOnly={readOnly}
          testId={`report-filter-to-${index}`}
          onChange={(event) => onChange({ to: event.target.value })}
        />
      ) : null}
      <div className="flex flex-wrap items-center gap-4">
        <SingleCheckbox
          label="Not"
          checked={condition.not}
          disabled={readOnly}
          testId={`report-filter-not-${index}`}
          onChange={(not) => onChange({ not })}
        />
        {STRING_OPERATORS.has(condition.operator) ? (
          <SingleCheckbox
            label="Match case"
            checked={condition.caseSensitive}
            disabled={readOnly}
            testId={`report-filter-case-${index}`}
            onChange={(caseSensitive) => onChange({ caseSensitive })}
          />
        ) : null}
        {readOnly ? null : (
          <SkyButton
            appearance="subtle"
            size="small"
            icon={DeleteIcon}
            aria-label={`Remove condition ${number}`}
            testId={`report-filter-remove-${index}`}
            onClick={onRemove}
          />
        )}
      </div>
    </fieldset>
  );
}
